import {
  COCO_API_BASE,
  COCO_API_KEY,
  COCO_SESSION_KEY,
  DEFAULT_COCO_ORIGIN,
} from '../config/cocoVoice';

let wakeAckObjectUrl = null;

export function getCocoSessionId() {
  try {
    return localStorage.getItem(COCO_SESSION_KEY) || '';
  } catch {
    return '';
  }
}

export function setCocoSessionId(sessionId) {
  if (!sessionId) return;
  try {
    localStorage.setItem(COCO_SESSION_KEY, sessionId);
  } catch {
    /* ignore quota */
  }
}

function buildHeaders(extra = {}) {
  const headers = { ...extra };
  if (COCO_API_KEY) headers['X-API-Key'] = COCO_API_KEY;
  return headers;
}

export function base64ToBlob(base64, mimeType = 'audio/mpeg') {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

/** 启动时预加载唤醒应答音，返回 Blob URL 供即时播放 */
export async function preloadWakeAckAudio() {
  if (wakeAckObjectUrl) return wakeAckObjectUrl;

  const res = await fetch(`${COCO_API_BASE}/v1/wake/ack`, {
    method: 'GET',
    headers: buildHeaders(),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok || !data?.audio_base64) {
    const msg = data?.detail || data?.message || `预加载应答音失败 (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : '预加载应答音失败');
  }

  const blob = base64ToBlob(data.audio_base64, data.mime_type || 'audio/mpeg');
  wakeAckObjectUrl = URL.createObjectURL(blob);
  return wakeAckObjectUrl;
}

export function getWakeAckObjectUrl() {
  return wakeAckObjectUrl;
}

export function revokeWakeAckAudio() {
  if (wakeAckObjectUrl) {
    URL.revokeObjectURL(wakeAckObjectUrl);
    wakeAckObjectUrl = null;
  }
}

/** 本地播放预缓存应答音（<50ms） */
export function playWakeAckUrl(url) {
  if (!url) return Promise.reject(new Error('应答音未加载'));
  const audio = new Audio(url);
  return audio.play().then(() => audio);
}

/** 通知服务端已进入唤醒态 */
export async function activateWakeOnServer() {
  const res = await fetch(`${COCO_API_BASE}/v1/wake/activate`, {
    method: 'POST',
    headers: buildHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ session_id: getCocoSessionId() }),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.detail || data?.message || `唤醒同步失败 (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : '唤醒同步失败');
  }

  if (data?.session_id) setCocoSessionId(data.session_id);
  return data;
}

export function normalizeVoiceResponse(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const detail = typeof raw.detail === 'string' ? raw.detail : '';
  return {
    session_id: raw.session_id || raw.sessionId || '',
    awake: raw.awake === true || raw.awake === 'true',
    response_text: raw.response_text || raw.responseText || raw.text || raw.reply || detail || '',
    hint: raw.hint || raw.message || '',
    transcript: raw.transcript || raw.stt_text || raw.recognized_text || '',
    audio_base64: raw.audio_base64 || raw.audioBase64 || '',
    mime_type: raw.mime_type || raw.mimeType || 'audio/mpeg',
    skip_wake_tts: raw.skip_wake_tts === true || raw.wake_only === true,
  };
}

function buildWsQueryString() {
  const params = new URLSearchParams();
  if (COCO_API_KEY) params.set('api_key', COCO_API_KEY);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** 直连 COCO 上游 WebSocket（大屏域名 Ingress 常不透传 WS） */
function buildDirectCocoListenWsUrl() {
  const wsOrigin = DEFAULT_COCO_ORIGIN.replace(/^http/i, 'ws');
  return `${wsOrigin}/api/v1/voice/listen${buildWsQueryString()}`;
}

/** 同源代理 WebSocket（本地 2020/3030 开发可用） */
function buildSameOriginCocoListenWsUrl() {
  if (typeof window === 'undefined' || !window.location?.host) return null;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/coco/v1/voice/listen${buildWsQueryString()}`;
}

/** 按优先级返回可尝试的 WebSocket 地址 */
export function buildCocoListenWsUrlCandidates() {
  const custom = import.meta.env.VITE_COCO_WS_URL;
  if (custom) return [custom];

  const direct = buildDirectCocoListenWsUrl();
  const sameOrigin = buildSameOriginCocoListenWsUrl();

  // 默认直连 COCO 上游；同源代理在部分浏览器/网络下 WS 握手会失败，仅作备选
  if (import.meta.env.VITE_COCO_WS_SAME_ORIGIN === 'true' && sameOrigin) {
    return [sameOrigin, direct];
  }
  if (sameOrigin && sameOrigin !== direct) return [direct, sameOrigin];
  return [direct];
}

export function buildCocoListenWsUrl() {
  return buildCocoListenWsUrlCandidates()[0];
}

export function playAudioBase64(audioBase64, mimeType = 'audio/mpeg') {
  return new Promise((resolve, reject) => {
    if (!audioBase64) {
      resolve(null);
      return;
    }
    const audio = new Audio(`data:${mimeType};base64,${audioBase64}`);
    audio.onended = () => resolve(audio);
    audio.onerror = () => reject(new Error('语音播放失败'));
    audio.play().catch(reject);
  });
}

export function isMicrophoneSupported() {
  return typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && (typeof AudioContext !== 'undefined' || typeof webkitAudioContext !== 'undefined');
}
